// `css` must come from @emotion/react, not @emotion/css: inside a `styled` template the react one
// serializes to styles, while the @emotion/css one returns a class-name STRING that lands in the
// output as invalid CSS. That is why the `styling` prop below silently did nothing.
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { BaseProperties, ColorVariables } from '@group-link-one/gl-ui/utilities';
import { motion } from 'framer-motion';
import type { IChildContTop, IStyling } from './types/Main.template.types';

const fadeIn = keyframes`
  to {
    opacity: 1;
    pointer-events: all;
    overflow: visible;
  }
`;

const fadeOut = keyframes`
  from {
    pointer-events: all;
    /* opacity: 1; */
    /* transform: scale(1); */
  }
  to {
    opacity: 0;
    pointer-events: none;
    /* transform: scale(0.8); */
  }
`;

const fadeInAllHeight = keyframes`
  to {
    opacity: 1;
    pointer-events: all;
    overflow: visible;
    height: 100%;
  }
`;

// interface WrapperContentProps {}

export default {
  /**
   * Shell: sidebar flush against the viewport edge, main column filling the rest. No padding or
   * gap here — the sidebar draws its own right border and the page header is sticky at the top
   * of the main column, exactly as in the redesign.
   */
  MainContainer: styled.div`
    min-height: 100vh;
    background: var(--color-bg);
    display: flex;

    > nav {
      z-index: 2;
    }

    /* The main column: its first child is the sticky page header. */
    > div {
      > :first-child {
        z-index: 2;
      }
    }
  `,

  RightContainer: styled.div`
    flex: 1;
    /*
      A flex item defaults to min-width:auto, which refuses to shrink below its content. Without
      this, any screen with a wide element (the BU table's 900px min-width, a code block) pushes
      the whole page into horizontal scroll instead of scrolling inside its own container.
    */
    min-width: 0;
    background-color: ${ColorVariables.BASE.BASE_COLOR_PRIMARY_BACKGROUND};
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &:has(.is-details-opened) {
      gap: 10px;
    }

    @media (max-width: 768px) {
      gap: 15px;
    }
  `,

  Content: styled.div`
    transition: all 0.4s ease-in-out;
    height: 100%;

    &.is-details-opened {
      position: relative;

      &.has-overflow-when-detailed-children-open {
        overflow: hidden;
      }

      .gl-wrapper-content {
        * {
          animation: ${fadeOut} 0.3s ease-in-out forwards;
        }
      }

      &:not(.just-child-content) .gl-wrapper-content-detailed {
        height: fit-content;
        animation: ${fadeIn} 0.3s 0.2s ease-in-out forwards;
      }

      &.just-child-content .gl-wrapper-content-detailed {
        height: 100%;
        animation: ${fadeInAllHeight} 0.3s 0.2s ease-in-out forwards;
      }
    }
  `,

  WrapperContent: styled.div<{ styling?: IStyling }>`
    background-color: ${ColorVariables.COLOR.COLOR_QUINARY_BACKGROUND};
    height: 100%;
    border: 1px solid ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};
    border-radius: 16px;
    padding: 20px;
    padding-bottom: 40px;

    display: flex;
    flex-direction: column;
    gap: 20px;

    transition: all 0.3s ease-in-out;

    &.gl-wrapper-content-detailed {
      position: absolute;
      opacity: 0;
      pointer-events: none;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }

    &.just-child-content {
      padding: 0px;
      border: none;
      gap: 0px;
    }

    ${({ styling }) => css`
      ${styling?.noBackgroundColor &&
      css`
        background-color: transparent;
      `}

      ${styling?.noBorder &&
      css`
        border: none;
      `}

      ${styling?.noPadding &&
      css`
        padding: 0px;
        padding-bottom: 40px;
      `}
    `}
  `,

  ChildCont: styled.div`
    width: 100%;
    background: transparent;
    flex: 1;

    display: flex;
    flex-direction: column;
  `,

  ChildContTop: styled(motion.div)<IChildContTop>`
    display: flex;
    gap: 20px;
    align-items: ${({ childContTopAlign }) => childContTopAlign || 'flex-end'};
    justify-content: space-between;

    margin-bottom: 20px;

    &.has-sticked {
      position: sticky;
      top: 5px;
      transition: all 0.3s ease-in-out;
    }

    @media (max-width: 768px) {
      gap: 10px;
    }

    @media (max-width: 600px) {
      flex-direction: column;
    }

    &.is-stuck {
      z-index: 9999;
      padding: 10px 15px;

      background-color: ${ColorVariables.BASE.BASE_COLOR_PRIMARY_BACKGROUND};
      border: 1px solid ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};
      border-radius: ${BaseProperties.RADIUS.SM};
      box-shadow: 0 3px 6px ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};

      &.has-sticked {
        .gl-page-title {
          height: 0px;
          opacity: 0;
        }
      }
    }
  `,
};
